import { useMemo } from "react";
import {
  useGetIntentContextTypesQuery,
  useGetIntentBuyersJourneyQuery,
  useGetIntentTopicsByIdsQuery,
} from "@/generated/taxonomy";
import { useQuery } from "@apollo/client";
import {
  SIMPLIFIED_PRODUCT_ID_QUERY,
  CLOUD_MATURITY_RANGE_QUERY,
} from "@/apis/taxonomy/queries";
import type { ProductsQueryPayload } from "@/types/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import { getHumanMinMax } from "@/lib/scoring/components/criteria/MinMaxEditor";
import { getBandRows } from "@/lib/scoring/utils";
import { getCriterionKey } from "@/lib/scoring/criterionRegistry";
import type {
  ScoringType,
  CriterionKey,
  BandRow,
  SignalLocationBand,
  ProductPresenceBand,
  ProductIntensityBand,
  ProductInstallAgeBand,
} from "@/lib/scoring/types";

function buildNameMap(
  data:
    | Array<{ id?: string | null; name?: string | null } | null | undefined>
    | null
    | undefined
): Map<string, string> {
  return new Map(
    (data ?? [])
      .filter(
        (item): item is { id: string; name: string } =>
          item != null && item.id != null && item.name != null
      )
      .map((item) => [item.id, item.name])
  );
}

function resolveIds(ids: string[], nameMap: Map<string, string>): string {
  return ids.map((id) => nameMap.get(id) ?? id).join(", ") || "—";
}

export interface ResolvedBandData {
  bandRows: BandRow[];
  contextHeader?: string;
  loading?: boolean;
}

/**
 * Returns human-readable BandRow[] + optional context header for any criterion.
 * Resolves IDs to names via taxonomy queries/context.
 * Falls back to getBandRows for unhandled criteria.
 */
export function useHumanReadableBandRows(
  criterion: ScoringType,
  key?: CriterionKey | null
): ResolvedBandData {
  const resolvedKey = key ?? getCriterionKey(criterion);

  // ── Intent taxonomy queries ─────────────────────────────────────────────
  const { data: contextTypesData } = useGetIntentContextTypesQuery({
    context: { clientName: TAXONOMY_CLIENT },
    skip: resolvedKey !== "contextType",
  });

  const { data: journeysData } = useGetIntentBuyersJourneyQuery({
    context: { clientName: TAXONOMY_CLIENT },
    skip: resolvedKey !== "buyersJourney",
  });

  // ── Intent topics query (all intent criteria have topics) ───────────────
  const intentTopicIds = useMemo(() => {
    if (
      !resolvedKey ||
      !["signalStrength", "contextType", "buyersJourney", "location"].includes(
        resolvedKey
      )
    )
      return [];
    const bands = (criterion[resolvedKey] as unknown[]) ?? [];
    const allIds = bands.flatMap(
      (b) => (b as { topics?: { ids?: string[] } }).topics?.ids ?? []
    );
    return [...new Set(allIds)];
  }, [criterion, resolvedKey]);

  const { data: topicsData } = useGetIntentTopicsByIdsQuery({
    variables: { ids: intentTopicIds as [string, ...string[]] },
    skip: intentTopicIds.length === 0,
    context: { clientName: TAXONOMY_CLIENT },
    fetchPolicy: "cache-first",
  });

  const topicNames = useMemo(() => {
    if (!topicsData?.intentTopic?.data) return "";
    return topicsData.intentTopic.data
      .filter(
        (t): t is NonNullable<typeof t> => t != null && t.displayName != null
      )
      .map((t) => t.displayName as string)
      .join(", ");
  }, [topicsData]);

  // ── Product name query ──────────────────────────────────────────────────
  const isProductCriterion =
    resolvedKey === "productIntensity" ||
    resolvedKey === "productInstallAgeMonths" ||
    resolvedKey === "products";

  const productIds = useMemo(() => {
    if (!isProductCriterion || !resolvedKey) return [];
    if (resolvedKey === "products") {
      const bands = criterion.products as ProductPresenceBand[] | undefined;
      return [...new Set(bands?.flatMap((b) => b.ids) ?? [])];
    }
    const bands = criterion[resolvedKey] as
      | (ProductIntensityBand | ProductInstallAgeBand)[]
      | undefined;
    return bands?.[0]?.productIds ?? [];
  }, [criterion, resolvedKey, isProductCriterion]);

  const { data: productsData, loading: productsLoading } =
    useQuery<ProductsQueryPayload>(SIMPLIFIED_PRODUCT_ID_QUERY, {
      variables: { ids: productIds },
      skip: productIds.length === 0,
      context: { clientName: TAXONOMY_CLIENT },
      fetchPolicy: "cache-first",
    });

  const productNameMap = useMemo(() => {
    if (!productsData?.product?.data) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const p of productsData.product.data) {
      if (p.id != null && p.name != null) map.set(Number(p.id), p.name);
    }
    return map;
  }, [productsData]);

  // ── Cloud Maturity range query ──────────────────────────────────────────
  const { data: cloudMaturityRangeData, loading: cloudMaturityLoading } =
    useQuery<{
      cloudMaturityRange: {
        data: Array<{ id: string; displayName: string } | null>;
      };
    }>(CLOUD_MATURITY_RANGE_QUERY, {
      context: { clientName: TAXONOMY_CLIENT },
      skip: resolvedKey !== "cloudMaturity",
    });

  const cloudMaturityRangeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of cloudMaturityRangeData?.cloudMaturityRange?.data ?? []) {
      if (r?.id && r?.displayName) map.set(r.id, r.displayName);
    }
    return map;
  }, [cloudMaturityRangeData]);

  // ── Taxonomy context (sync) ─────────────────────────────────────────────
  const {
    countries,
    hgIndustryNameMap,
    faiDepartmentMap,
    faiRoleMap,
    vendors,
    productCategories,
    spendCategories,
  } = useTaxonomyContext();

  // ── Helper: resolve product names as string ─────────────────────────────
  const productNamesString = useMemo(() => {
    if (productIds.length === 0) return "";
    return productIds
      .map((id) => productNameMap.get(id))
      .filter(Boolean)
      .join(", ");
  }, [productIds, productNameMap]);

  // ── Helper: topics context header for intent criteria ───────────────────
  const topicsHeader = topicNames ? `Topics: ${topicNames}` : undefined;

  const isLoading =
    (isProductCriterion && productsLoading) ||
    (resolvedKey === "cloudMaturity" && cloudMaturityLoading);

  // ── Resolve ─────────────────────────────────────────────────────────────
  const resolved = useMemo((): ResolvedBandData => {
    if (!resolvedKey) return { bandRows: getBandRows(criterion) };

    // ── Intent criteria ───────────────────────────────────────────────────
    if (resolvedKey === "signalStrength") {
      return {
        contextHeader: topicsHeader,
        bandRows: (criterion.signalStrength ?? []).map((b) => ({
          summary: getHumanMinMax(b.signalScore?.min, b.signalScore?.max),
          score: b.score,
        })),
      };
    }

    if (resolvedKey === "contextType") {
      const nameMap = buildNameMap(contextTypesData?.intentContextType?.data);
      return {
        contextHeader: topicsHeader,
        bandRows: (criterion.contextType ?? []).map((b) => ({
          summary: resolveIds(b.contextTypeIds ?? [], nameMap),
          score: b.score,
        })),
      };
    }

    if (resolvedKey === "buyersJourney") {
      const nameMap = buildNameMap(journeysData?.intentBuyersJourney?.data);
      return {
        contextHeader: topicsHeader,
        bandRows: (criterion.buyersJourney ?? []).map((b) => ({
          summary: resolveIds(b.buyersJourneyIds ?? [], nameMap),
          score: b.score,
        })),
      };
    }

    if (resolvedKey === "location") {
      const countryMap = new Map(
        countries
          .filter((c) => c.alpha2 && c.name)
          .map((c) => [c.alpha2!, c.name!])
      );
      return {
        contextHeader: topicsHeader,
        bandRows: (criterion.location ?? []).map((b: SignalLocationBand) => {
          const { countryAlpha2s, regionNames, stateIds } = b.location;
          if (stateIds?.length)
            return {
              summary: `${stateIds.length} state/province(s)`,
              score: b.score,
            };
          if (regionNames?.length)
            return { summary: regionNames.join(", "), score: b.score };
          if (countryAlpha2s?.length)
            return {
              summary: resolveIds(countryAlpha2s, countryMap),
              score: b.score,
            };
          return { summary: "—", score: b.score };
        }),
      };
    }

    // ── Fit criteria: Geography ───────────────────────────────────────────
    if (resolvedKey === "geography") {
      const countryMap = new Map(
        countries
          .filter((c) => c.alpha2 && c.name)
          .map((c) => [c.alpha2!, c.name!])
      );
      const bands = criterion.geography ?? [];
      const [firstBand] = bands;
      const geoType = firstBand?.countryAlpha2s?.length
        ? "Countries"
        : firstBand?.regionNames?.length
          ? "Regions"
          : firstBand?.stateIds?.length
            ? "States"
            : undefined;
      return {
        contextHeader: geoType ? `Geography Type: ${geoType}` : undefined,
        bandRows: bands.map((b) => {
          if (b.countryAlpha2s?.length)
            return {
              summary: resolveIds(b.countryAlpha2s, countryMap),
              score: b.score,
            };
          if (b.regionNames?.length)
            return { summary: b.regionNames.join(", "), score: b.score };
          if (b.stateIds?.length)
            return {
              summary: `${b.stateIds.length} state/province(s)`,
              score: b.score,
            };
          return { summary: "—", score: b.score };
        }),
      };
    }

    // ── Fit criteria: Industry ────────────────────────────────────────────
    if (resolvedKey === "industry") {
      return {
        bandRows: (criterion.industry ?? []).map((b) => {
          const names: string[] = [];
          if (b.industryIds?.length) {
            names.push(
              ...b.industryIds.map(
                (id) => hgIndustryNameMap.get(id) ?? String(id)
              )
            );
          }
          return {
            summary: names.length > 0 ? names.join(", ") : "—",
            score: b.score,
          };
        }),
      };
    }

    // ── Fit criteria: Global Presence ─────────────────────────────────────
    if (resolvedKey === "globalPresence") {
      const countryMap = new Map(
        countries
          .filter((c) => c.alpha2 && c.name)
          .map((c) => [c.alpha2!, c.name!])
      );
      const rows: BandRow[] = [];
      for (const b of criterion.globalPresence ?? []) {
        if (b.isMultiNational) {
          rows.push({ summary: "Are Multinational", score: b.score });
        }
        if (b.hqCountryCodes?.length) {
          rows.push({
            summary: `Has headquarters in ${resolveIds(b.hqCountryCodes, countryMap)}`,
            score: b.score,
          });
        }
        if (!b.isMultiNational && !b.hqCountryCodes?.length) {
          rows.push({ summary: "—", score: b.score });
        }
      }
      return { bandRows: rows };
    }

    // ── Fit criteria: Product Intensity / Product Install Age ──────────
    if (
      resolvedKey === "productIntensity" ||
      resolvedKey === "productInstallAgeMonths"
    ) {
      return {
        contextHeader: productNamesString
          ? `Products: ${productNamesString}`
          : undefined,
        bandRows: getBandRows(criterion),
      };
    }

    // ── Fit criteria: Product Presence ────────────────────────────────────
    if (resolvedKey === "products") {
      return {
        bandRows: (criterion.products ?? []).map(
          (band: ProductPresenceBand) => {
            const method =
              band.inclusionMethod === "ALL_PRESENT" ? "all" : "any";
            const names = band.ids
              .map((id) => productNameMap.get(id))
              .filter(Boolean)
              .join(", ");
            const summary = names
              ? `Has ${method} of ${names}`
              : `Has ${method} of ${band.ids.length} product${band.ids.length !== 1 ? "s" : ""}`;

            // Resolve FAI
            const faiIds = [
              ...(band.departmentIds ?? []),
              ...(band.roleIds ?? []),
            ];
            let subtitle: string | undefined;
            if (faiIds.length > 0) {
              const faiNames = faiIds
                .map((id) => {
                  const dept = faiDepartmentMap.get(id);
                  if (dept) return dept.name;
                  const roleEntry = faiRoleMap.get(id);
                  if (roleEntry) return roleEntry.role.name;
                  return null;
                })
                .filter(Boolean);
              if (faiNames.length > 0) {
                subtitle = `Used by ${faiNames.join(", ")}`;
              }
            }

            return { summary, score: band.score, subtitle };
          }
        ),
      };
    }

    // ── Fit criteria: Cloud Maturity ──────────────────────────────────────
    if (resolvedKey === "cloudMaturity") {
      return {
        bandRows: (criterion.cloudMaturity ?? []).map((b) => {
          const rangeId = b.fullTechStackRangeId ?? b.newServicesRangeId;
          const segment = b.fullTechStackRangeId
            ? "Full Tech Stack"
            : "Recently Added Services";
          const rangeName = rangeId
            ? (cloudMaturityRangeMap.get(rangeId) ?? "—")
            : "—";
          return {
            summary: `${rangeName}`,
            score: b.score,
            subtitle: `Tech Stack Segment: ${segment}`,
          };
        }),
      };
    }

    // ── Fit criteria: Vendor Penetration ──────────────────────────────────
    if (resolvedKey === "vendorPenetration") {
      const vendorId = criterion.vendorPenetration?.[0]?.vendorId;
      const vendorName = vendorId
        ? (vendors.find((v) => v.id === vendorId)?.name ?? String(vendorId))
        : null;
      return {
        contextHeader: vendorName ? `Vendor: ${vendorName}` : undefined,
        bandRows: getBandRows(criterion),
      };
    }

    // ── Fit criteria: Category Penetration ────────────────────────────────
    if (resolvedKey === "productCategoryPenetration") {
      const categoryId = criterion.productCategoryPenetration?.[0]?.categoryId;
      const categoryName = categoryId
        ? (productCategories.find((c) => String(c.id) === categoryId)?.name ??
          categoryId)
        : null;
      return {
        contextHeader: categoryName
          ? `Product Category: ${categoryName}`
          : undefined,
        bandRows: getBandRows(criterion),
      };
    }

    // ── Fit criteria: IT Spend ────────────────────────────────────────────
    if (resolvedKey === "spend") {
      const categoryId = criterion.spend?.[0]?.categoryId;
      const categoryName = categoryId
        ? (spendCategories.find((c) => c.id === categoryId)?.name ?? categoryId)
        : null;
      return {
        contextHeader: categoryName ? `Category: ${categoryName}` : undefined,
        bandRows: getBandRows(criterion),
      };
    }

    // ── Fallback ─────────────────────────────────────────────────────────
    return { bandRows: getBandRows(criterion) };
  }, [
    resolvedKey,
    criterion,
    contextTypesData,
    journeysData,
    topicsHeader,
    countries,
    hgIndustryNameMap,
    faiDepartmentMap,
    faiRoleMap,
    productNameMap,
    productNamesString,
    cloudMaturityRangeMap,
    vendors,
    productCategories,
    spendCategories,
  ]);

  return { ...resolved, loading: isLoading };
}
